import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import { FormLayout, TextField, Form, Button, Banner } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { createTrainingTest } from "services/work-security-training.service";
import { WorkSecurityArea } from "services/work-security-areas.service";
import { setIsModalxOpen, setDestroy } from "components/modalx/modalx.slice";
import AreaFormField, {
  defaultAreaSelection,
} from "features/work-security-areas/AreaFormField";
import { resolveAreaErrorMessage } from "features/work-security-areas/area-errors";

interface Props {
  /** Accessible spheres (PR-2 §P4) — a Select is only offered in "all spheres" mode. */
  areas?: WorkSecurityArea[];
  /** The module header's concretely-selected sphere; null/undefined = "all spheres" mode. */
  selectedArea?: WorkSecurityArea | null;
  onSuccess?: () => void;
}

export default function TrainingTestFormModal({
  areas = [],
  selectedArea,
  onSuccess,
}: Props) {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [passPercent, setPassPercent] = useState("80");
  const [durationMinutes, setDurationMinutes] = useState("30");
  const [maxAttempts, setMaxAttempts] = useState("");
  const [selectedAreaId, setSelectedAreaId] = useState(() => defaultAreaSelection(areas));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const showAreaField = areas.length > 1;

  const close = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  const handleSubmit = async () => {
    if (!companyId) return;
    if (!name.trim()) {
      setError(t("name_required", { defaultValue: "Name is required" }));
      return;
    }
    setSaving(true);
    setError("");
    try {
      const { test } = await createTrainingTest(companyId, {
        name: name.trim(),
        description: description.trim() || undefined,
        passPercent: Number(passPercent) || 80,
        durationMinutes: Number(durationMinutes) || 30,
        maxAttempts: maxAttempts.trim() ? Number(maxAttempts) : null,
        // HARD RULE (PR-2 §P1): omit `area` entirely with 0-1 accessible
        // spheres, so this request stays byte-for-byte what it was before
        // spheres existed.
        ...(showAreaField
          ? { area: selectedArea ? selectedArea.id : selectedAreaId }
          : {}),
      });
      close();
      onSuccess?.();
      navigate(`/app/${companyId}/work-security/training/${test.id}`);
    } catch (err: any) {
      setError(
        resolveAreaErrorMessage(err, t) ||
          err?.response?.data?.message ||
          t("create_failed", { defaultValue: "Failed to create test" })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <FormLayout>
        {error && <Banner tone="critical">{error}</Banner>}
        <TextField
          label={t("name", { defaultValue: "Name" })}
          value={name}
          onChange={setName}
          autoComplete="off"
          autoFocus
        />
        <TextField
          label={t("description", { defaultValue: "Description" })}
          value={description}
          onChange={setDescription}
          multiline={3}
          autoComplete="off"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <TextField
            label={t("pass_percent", { defaultValue: "Pass %" })}
            type="number"
            value={passPercent}
            onChange={setPassPercent}
            autoComplete="off"
            min={0}
            max={100}
          />
          <TextField
            label={t("duration_minutes", { defaultValue: "Duration (min)" })}
            type="number"
            value={durationMinutes}
            onChange={setDurationMinutes}
            autoComplete="off"
            min={1}
          />
          <TextField
            label={t("max_attempts", {
              defaultValue: "Max attempts (empty = unlimited)",
            })}
            type="number"
            value={maxAttempts}
            onChange={setMaxAttempts}
            autoComplete="off"
            min={1}
          />
        </div>
        <AreaFormField
          areas={areas}
          selectedArea={selectedArea}
          value={selectedAreaId}
          onChange={setSelectedAreaId}
          disabled={saving}
        />
        <div className="flex justify-end gap-2">
          <Button onClick={close}>{t("cancel", { defaultValue: "Cancel" })}</Button>
          <Button variant="primary" submit loading={saving}>
            {t("create", { defaultValue: "Create" })}
          </Button>
        </div>
      </FormLayout>
    </Form>
  );
}
